<template>
  <div class="h-full flex flex-col">
    <!-- Search Bar -->
    <div class="px-3 py-2">
      <div class="relative">
        <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400"></i>
        <input 
          v-model="filterSearch"
          type="text"
          placeholder="Search clients..."
          class="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-200 dark:border-dark-600 bg-white/50 dark:bg-dark-700/50 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
        />
      </div>
    </div>

    <!-- Clients List -->
    <div class="flex-1 overflow-y-auto px-3 space-y-2">
      <div v-for="client in paginatedClients" :key="client.id"
        class="group relative p-2.5 rounded-lg bg-gradient-to-br from-white/50 to-white/30 dark:from-dark-700/50 dark:to-dark-700/30 border border-green-200/50 dark:border-green-800/30 hover:border-green-300 dark:hover:border-green-700 hover:shadow-md transition-all duration-200 cursor-pointer"
        @click="viewClientOrders(client)">
        
        <div class="space-y-1">
          <div class="flex items-start justify-between gap-2">
            <h4 class="text-xs font-semibold text-gray-900 dark:text-white truncate flex-1">{{ client.name }}</h4>
            <span class="text-xs font-bold whitespace-nowrap" :class="getOutstandingClass(client.outstanding)">
              {{ formatCurrency(client.outstanding) }}
            </span>
          </div>
          
          <div class="flex items-center justify-between text-[10px]">
            <span class="flex items-center gap-1">
              <div class="w-1.5 h-1.5 rounded-full" :class="client.pendingOrders > 0 ? 'bg-yellow-500' : 'bg-green-500'"></div>
              <span :class="client.pendingOrders > 0 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'" class="font-medium">
                {{ client.pendingOrders }} pending
              </span>
            </span>
            <span class="text-gray-500 dark:text-gray-400 text-[9px]">{{ client.lastPayment }}</span>
          </div>
        </div>

        <!-- Hover Effect -->
        <div class="absolute inset-0 rounded-lg bg-gradient-to-br from-green-400/0 to-emerald-400/0 group-hover:from-green-400/5 group-hover:to-emerald-400/5 transition-all pointer-events-none"></div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredData.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
        <div class="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mb-2">
          <i class="fas fa-users text-lg text-green-600 dark:text-green-400"></i>
        </div>
        <p class="text-xs font-medium text-gray-900 dark:text-white">No clients found</p>
        <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Try adjusting your search</p>
      </div>
    </div>

    <!-- View All Button -->
    <div class="p-3 border-t border-gray-200/50 dark:border-dark-700/50">
      <button
        @click="$emit('view-all')"
        class="w-full px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200"
      >
        View All {{ filteredData.length }} Clients
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { formatCurrency } from '@/utils/formatters';

const props = defineProps({
  clients: {
    type: Array,
    default: () => []
  },
  defaultSort: {
    type: String,
    default: 'amount_high'
  },
  perPage: {
    type: Number,
    default: 5
  }
});

const emit = defineEmits(['view-client-orders', 'view-all']);

const filterSearch = ref('');
const filterSort = ref(props.defaultSort);
const currentPage = ref(1);

// Reset pagination when filters change
watch([filterSearch, filterSort], () => {
  currentPage.value = 1;
}, { deep: true });

// Apply filters and sorting
const filteredData = computed(() => {
  let filtered = [...props.clients];
  
  // Apply search filter
  if (filterSearch.value.trim()) {
    const searchTerm = filterSearch.value.toLowerCase();
    filtered = filtered.filter(client => 
      client.name.toLowerCase().includes(searchTerm)
    );
  }
  
  // Apply sorting
  filtered.sort((a, b) => {
    switch (filterSort.value) {
      case 'amount_high':
        return b.outstanding - a.outstanding;
      case 'amount_low':
        return a.outstanding - b.outstanding;
      case 'orders_high':
        return b.pendingOrders - a.pendingOrders;
      case 'orders_low':
        return a.pendingOrders - b.pendingOrders;
      case 'recent_payment':
        return new Date(b.lastPayment) - new Date(a.lastPayment);
      case 'oldest_payment':
        return new Date(a.lastPayment) - new Date(b.lastPayment);
      default:
        return 0;
    }
  });
  
  return filtered;
});

// Paginate the data
const paginatedClients = computed(() => {
  const startIndex = (currentPage.value - 1) * props.perPage;
  return filteredData.value.slice(startIndex, startIndex + props.perPage);
});

const viewClientOrders = (client) => {
  emit('view-client-orders', client);
};

const getOutstandingClass = (amount) => {
  if (amount >= 100000) return 'text-red-600';
  if (amount >= 50000) return 'text-yellow-600';
  return 'text-green-600';
};
</script> 